import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';

import { FileUrlModel } from '../shared/models/file-url.model';
import { UploadErrorType, UploadService, UploadSession, UploadSessionBase, UploadSessionState, UploadSource } from './models/upload-session.model';
import { AppSpecificUploadResources } from './upload-config.service';

@Injectable({
    providedIn: 'root'
})
export class UploadManagerService {

    public uploadResources: Observable<AppSpecificUploadResources>;
    private readonly _uploadResources: BehaviorSubject<AppSpecificUploadResources>;
    public uploadSession: Observable<UploadSessionBase>;
    private readonly _uploadSession: BehaviorSubject<UploadSessionBase>;
    private uploadInProgress: UploadSessionBase;
    private currentSubscription: Subscription;

    constructor() {
        this._uploadResources = new BehaviorSubject<AppSpecificUploadResources>(null);
        this.uploadResources = this._uploadResources.asObservable();
        this._uploadSession = new BehaviorSubject<UploadSessionBase>(null);
        this.uploadSession = this._uploadSession.asObservable();
    }

    public upload<TResult>(file: File, uploadService: UploadService<TResult>, sessionId: string, resources: AppSpecificUploadResources, uploadSource: UploadSource): Observable<UploadSession<TResult>> {
        if (this.uploadInProgress && !this.uploadInProgress.finished()) {
            return null;
        }

        if (this.currentSubscription) {
            this.currentSubscription.unsubscribe();
            this.currentSubscription = null;
            this.uploadInProgress = null;
        }

        const uploadSession = uploadService.upload(file, sessionId, uploadSource);
        if (uploadSession) {
            this.currentSubscription = uploadSession.subscribe(session => {
                this._uploadResources.next(resources);
                this.uploadInProgress = session;
                this._uploadSession.next(session);
            });
        }

        return uploadSession;
    }

    public uploadFiles(files: FileList, sessionId: string, resources: AppSpecificUploadResources, uploadSource: UploadSource): Observable<UploadSession<FileUrlModel>> {
        if (this.uploadInProgress && !this.uploadInProgress.finished()) {
            return null;
        }

        if (this.currentSubscription) {
            this.currentSubscription.unsubscribe();
            this.currentSubscription = null;
            this.uploadInProgress = null;
        }

        const uploadSession = new UploadSession<FileUrlModel>(sessionId, files[0], uploadSource);
        const uploadObservable = new BehaviorSubject<UploadSession<FileUrlModel>>(uploadSession);

        uploadSession.cancellationToken.onCancel(() => {
            uploadSession.state = UploadSessionState.Cancelled;
            uploadObservable.next(uploadSession);
        });

        // Multi upload is not supported, so fail with appropriate error
        uploadSession.error = {
            title: resources.multipleFilesErrorTitle,
            type: UploadErrorType.MultipleFiles,
            message: resources.multipleFiles
        };

        uploadSession.state = UploadSessionState.Failed;
        uploadObservable.next(uploadSession);

        this.currentSubscription = uploadObservable.subscribe(session => {
            this._uploadResources.next(resources);
            this.uploadInProgress = session;
            this._uploadSession.next(session);
        });

        return uploadObservable.asObservable();
    }

    public cancelUpload() {
        if (this.uploadInProgress) {
            this.uploadInProgress.cancellationToken.cancel();
        }
    }

}
